import { useEffect, useRef } from "react";

const SVGNS = "http://www.w3.org/2000/svg";

export function Helix() {
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    host.innerHTML = "";
    const svg = document.createElementNS(SVGNS, "svg");
    svg.setAttribute("viewBox", "0 0 520 520");
    svg.style.overflow = "visible";
    host.appendChild(svg);

    const cx = 260, cy = 260, amp = 130, stepY = 22, turnSpacing = 7, pairs = 22;
    const startY = cy - (pairs * stepY) / 2;

    const sA = document.createElementNS(SVGNS, "path");
    sA.setAttribute("stroke", "url(#dz-strandL)");
    sA.setAttribute("stroke-width", "4.5");
    sA.setAttribute("fill", "none");
    sA.setAttribute("stroke-linecap", "round");
    svg.appendChild(sA);

    const sB = document.createElementNS(SVGNS, "path");
    sB.setAttribute("stroke", "url(#dz-strandR)");
    sB.setAttribute("stroke-width", "4.5");
    sB.setAttribute("fill", "none");
    sB.setAttribute("stroke-linecap", "round");
    svg.appendChild(sB);

    interface Rung { rung: SVGLineElement; a: SVGCircleElement; b: SVGCircleElement; y: number; baseT: number; }
    const arr: Rung[] = [];
    for (let i = 0; i < pairs; i++) {
      const rung = document.createElementNS(SVGNS, "line");
      rung.setAttribute("stroke-linecap", "round");
      svg.appendChild(rung);
      const a = document.createElementNS(SVGNS, "circle"); a.setAttribute("fill", "#76C893"); svg.appendChild(a);
      const b = document.createElementNS(SVGNS, "circle"); b.setAttribute("fill", "#168AAD"); svg.appendChild(b);
      arr.push({ rung, a, b, y: startY + i * stepY, baseT: (i / turnSpacing) * Math.PI * 2 });
    }

    let phase = 0;
    let last = performance.now();
    let running = true;
    let raf = 0;

    function frame(now: number) {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      phase += dt * 0.5;

      const samples = pairs * 3;
      let dA = "", dB = "";
      for (let i = 0; i <= samples; i++) {
        const f = i / samples;
        const t = (f * (pairs - 1) / turnSpacing) * Math.PI * 2 + phase;
        const y = startY + f * (pairs - 1) * stepY;
        const xA = cx + Math.cos(t) * amp;
        const xB = cx + Math.cos(t + Math.PI) * amp;
        dA += (i ? "L" : "M") + xA.toFixed(1) + "," + y.toFixed(1) + " ";
        dB += (i ? "L" : "M") + xB.toFixed(1) + "," + y.toFixed(1) + " ";
      }
      sA.setAttribute("d", dA);
      sB.setAttribute("d", dB);

      for (let i = 0; i < pairs; i++) {
        const p = arr[i];
        const t = p.baseT + phase;
        const xA = cx + Math.cos(t) * amp;
        const xB = cx + Math.cos(t + Math.PI) * amp;
        const dA2 = (Math.sin(t) + 1) / 2;
        const dB2 = (Math.sin(t + Math.PI) + 1) / 2;
        const vis = Math.abs(Math.sin(t));
        p.rung.setAttribute("x1", String(xA));
        p.rung.setAttribute("y1", String(p.y));
        p.rung.setAttribute("x2", String(xB));
        p.rung.setAttribute("y2", String(p.y));
        p.rung.setAttribute("stroke", "rgba(14,58,102,1)");
        p.rung.setAttribute("stroke-opacity", (0.05 + vis * 0.16).toFixed(3));
        p.rung.setAttribute("stroke-width", (1.6 + vis * 1.6).toFixed(2));
        p.a.setAttribute("cx", xA.toFixed(1));
        p.a.setAttribute("cy", String(p.y));
        p.a.setAttribute("r", (3.6 + dA2 * 4.0).toFixed(2));
        p.a.setAttribute("fill-opacity", (0.45 + dA2 * 0.55).toFixed(3));
        p.b.setAttribute("cx", xB.toFixed(1));
        p.b.setAttribute("cy", String(p.y));
        p.b.setAttribute("r", (3.6 + dB2 * 4.0).toFixed(2));
        p.b.setAttribute("fill-opacity", (0.45 + dB2 * 0.55).toFixed(3));
      }
      if (running) raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      host.innerHTML = "";
    };
  }, []);

  return <div ref={hostRef} className="dz-helix" />;
}

export function HelixDefs() {
  return (
    <svg width={0} height={0} style={{ position: "absolute" }} aria-hidden="true">
      <defs>
        <linearGradient id="dz-strandL" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#76C893" stopOpacity="0.95" />
          <stop offset="1" stopColor="#76C893" stopOpacity="0.5" />
        </linearGradient>
        <linearGradient id="dz-strandR" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#168AAD" stopOpacity="0.95" />
          <stop offset="1" stopColor="#168AAD" stopOpacity="0.5" />
        </linearGradient>
      </defs>
    </svg>
  );
}

